import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Syringe,
  CheckCircle2,
  AlertTriangle,
  Stethoscope,
  User,
  Shield,
  Zap,
  Wind,
  AlertOctagon,
  Download,
  RotateCcw,
  Baby,
  Sparkles,
  Loader2,
  Activity
} from 'lucide-react';

import { API_URL } from '../config';

export default function IntubationPediatricPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('intubation-pediatric-full');
      return saved ? JSON.parse(saved) : {
        patient: { poids: '', age: '' },
        checks: {},
        medicaments: {
          atropine: { ok: false, dose: '' },
          ketamine: { ok: false, dose: '' },
          celocurine: { ok: false, dose: '' },
          midazolam: { ok: false, debit: '0.3' },
          sufentanil: { ok: false, debit: '0.3' }
        },
        materiel: {},
        equipement: {
          sondeIntubation: '',
          lame: '',
          tuyaux: 'pédiatrique',
          vt: '',
          fr: '',
          sondeGastrique: ''
        },
        postiot: {
          chiffreArcade: '',
          pressionBallonnet: '',
          saturation: '',
          etco2: '',
          pas: '',
          pad: ''
        },
        controles: {},
        recapPositologies: ''
      };
    } catch {
      return {
        patient: { poids: '', age: '' },
        checks: {},
        medicaments: {
          atropine: { ok: false, dose: '' },
          ketamine: { ok: false, dose: '' },
          celocurine: { ok: false, dose: '' },
          midazolam: { ok: false, debit: '0.3' },
          sufentanil: { ok: false, debit: '0.3' }
        },
        materiel: {},
        equipement: {
          sondeIntubation: '',
          lame: '',
          tuyaux: 'pédiatrique',
          vt: '',
          fr: '',
          sondeGastrique: ''
        },
        postiot: {
          chiffreArcade: '',
          pressionBallonnet: '',
          saturation: '',
          etco2: '',
          pas: '',
          pad: ''
        },
        controles: {},
        recapPositologies: ''
      };
    }
  });

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('intubation-pediatric-full', JSON.stringify(formData));
  }, [formData]);

  // Calculate doses and equipment based on weight
  useEffect(() => {
    if (!formData.patient.poids) return;
    
    const poids = parseFloat(formData.patient.poids);
    if (isNaN(poids) || poids <= 0) return;
    
    const ageMonths = formData.patient.age ? parseInt(formData.patient.age) : 0;
    
    // Médicaments
    const atropineDose = Math.round(poids * 20); // µg
    const ketamineDose = Math.round(poids * (ageMonths < 18 ? 4 : 2)); // mg
    const celocurineDose = Math.round(poids * (ageMonths < 24 ? 2 : 1)); // mg
    const midazolamDebit = (poids / 10).toFixed(1); // mg/h
    const sufentanilDebit = (poids / 10).toFixed(1); // µg/h
    
    // Équipement
    const sondeSize = poids < 2 ? '2.5' : poids < 4 ? '3' : poids < 7 ? '3.5' : poids < 12 ? '4' : poids < 20 ? '4.5' : '5';
    const lameType = poids < 4 ? 'Miller 0' : poids < 10 ? 'Miller 1' : poids < 20 ? 'Macintosh 2' : 'Macintosh 3';
    const vt = Math.round(poids * 6); // mL (6 mL/kg)
    const fr = poids < 4 ? '30-40' : poids < 10 ? '24-30' : poids < 20 ? '20-25' : '16-20';
    const sondeGastrique = Math.round(poids * 2); // taille sonde gastrique
    
    // Remplissage
    const nacl = Math.round(poids * 10); // mL (10 mL/kg)
    const noradrenaline = Math.round(poids * 0.1 * 60); // µg/h (0.1 µg/kg/min)
    
    setFormData(prev => ({
      ...prev,
      medicaments: {
        ...prev.medicaments,
        atropine: { ...prev.medicaments.atropine, dose: `${atropineDose} µg (${(atropineDose/1000).toFixed(2)} mg)` },
        ketamine: { ...prev.medicaments.ketamine, dose: `${ketamineDose} mg` },
        celocurine: { ...prev.medicaments.celocurine, dose: `${celocurineDose} mg` },
        midazolam: { ...prev.medicaments.midazolam, debit: midazolamDebit },
        sufentanil: { ...prev.medicaments.sufentanil, debit: sufentanilDebit }
      },
      equipement: {
        ...prev.equipement,
        sondeIntubation: sondeSize,
        lame: lameType,
        vt: vt.toString(),
        fr: fr,
        sondeGastrique: sondeGastrique.toString()
      },
      hemodynamique: {
        nacl: nacl.toString(),
        noradrenaline: noradrenaline.toString()
      }
    }));
  }, [formData.patient.poids, formData.patient.age]);

  const handleCheck = (section, key) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: !prev[section][key] }
    }));
  };

  const handleReset = () => {
    if (window.confirm('Réinitialiser toute la checklist ?')) {
      localStorage.removeItem('intubation-pediatric-full');
      window.location.reload();
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const opt = {
        margin: 10,
        filename: `Intubation-Pediatrique-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('PDF exporté');
    } catch (error) {
      toast.error('Erreur export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    if (!formData.patient.poids) {
      toast.error('Veuillez entrer le poids du patient');
      return;
    }
    
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-pediatric-intubation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur analyse');
      
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="space-y-2 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div className="flex items-center gap-2">
            <Baby className="w-6 h-6 md:w-8 md:h-8 text-pink-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--text))]">Intubation Pédiatrique</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Check-list - Aide cognitive</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing || !formData.patient.poids} size="sm" className="bg-purple-400/20 text-purple-300 border-purple-400/30">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              <span className="ml-1 hidden sm:inline">IA</span>
            </Button>
            <Button onClick={handleReset} size="sm" variant="outline" className="text-amber-300 border-amber-400/30">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button onClick={handleExportPDF} size="sm" className="bg-cyan-400/20 text-cyan-300 border-cyan-400/30">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Alert className="bg-pink-400/10 border-pink-400/30 p-2">
          <Baby className="h-4 w-4 text-pink-400" />
          <AlertDescription className="text-xs text-[hsl(var(--text))]">
            <strong>Pédiatrie :</strong> Dosages calculés automatiquement selon poids et âge
          </AlertDescription>
        </Alert>
      </div>

      <div ref={printRef} className="space-y-3">
        {/* PATIENT */}
        <Card className="border-pink-400/30 bg-pink-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-pink-300 flex items-center gap-2">
              <User className="w-4 h-4" />
              PATIENT
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Poids (kg) *</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.patient.poids}
                  onChange={(e) => setFormData(prev => ({ ...prev, patient: { ...prev.patient, poids: e.target.value } }))}
                  className="h-9 text-sm font-bold"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Âge (mois)</label>
                <Input
                  type="number"
                  value={formData.patient.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, patient: { ...prev.patient, age: e.target.value } }))}
                  className="h-9 text-sm"
                  placeholder="naissance"
                />
                <p className="text-[10px] text-[hsl(var(--text-dim))] mt-0.5">d'après le poids renseigné</p>
              </div>
            </div>

            {[
              { id: 'march', label: 'Examen MARCH/xABCDE' },
              { id: 'preoxygenation', label: 'Préoxygénation', detail: 'BAVU, masque adapté (si hypoxie: ventilation douce)' },
              { id: 'collier', label: 'Collier cervical, maintien du rachis' },
              { id: 'ablation', label: 'Ablation matériel', detail: 'fracas dentaire, aspiration' },
              { id: 'installation', label: 'Installation/ergonomie', detail: 'Hauteur brancard, Position NEUTRE tête' },
              { id: 'scope', label: 'Scope', detail: 'visible, 4 brins, SpO₂, EtCO₂, PA/2min' },
              { id: 'info_parents', label: 'Information aux parents' }
            ].map(({ id, label, detail }) => (
              <div key={id} className="flex items-start gap-2 p-2 rounded bg-[hsl(var(--bg-elev))] cursor-pointer" onClick={() => handleCheck('checks', id)}>
                <Checkbox checked={formData.checks[id] || false} className="mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs md:text-sm text-[hsl(var(--text))]">{label}</div>
                  {detail && <div className="text-[10px] text-[hsl(var(--text-dim))] mt-0.5">{detail}</div>}
                </div>
              </div>
            ))}

            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="text-xs font-semibold text-amber-300 mb-1.5">Prédire IOT difficile</div>
              <div className="space-y-0.5 text-[10px] md:text-xs text-[hsl(var(--text-dim))]">
                <div className="text-red-300">• ANESTHÉSIE INSUFFISANTE (spasme laryngé)</div>
                <div>• ATCD IOT difficile</div>
                <div>• Dysmorphie faciale</div>
                <div>• Ouverture buccale &lt;3 travers doigts</div>
                <div>• SAOS, Stridor connu</div>
                <div>• Trauma face/saignement intrabuccal</div>
              </div>
            </div>

            <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="text-xs font-semibold text-purple-300 mb-1">Optimisation hémodynamique (si besoin)</div>
              <div className="text-[10px] text-[hsl(var(--text-dim))] space-y-0.5">
                <div>• VVP/KTIO</div>
                <div>• Remplissage NaCl: {formData.hemodynamique?.nacl || '?'} mL (10 mL/kg)</div>
                <div>• Noradrénaline: {formData.hemodynamique?.noradrenaline || '?'} µg/h (0,1 µg/kg/min)</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MATÉRIEL */}
        <Card>
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-cyan-300 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              MATÉRIEL = BASIC
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {[
              { id: 'bavu', label: 'BAVU + EtCO₂, Masque, Filtres' },
              { id: 'aspiration', label: 'Aspiration (taille sonde selon âge/poids)' },
              { id: 'plateau', label: 'PLATEAU : Sonde mandrinée, Seringue 10cc, Laryngo, Lame, Gel, Stéthoscope' },
              { id: 'respirateur', label: 'Respirateur mode pédiatrie (si <20kg circuit pédiatrique)' }
            ].map(({ id, label }) => (
              <div key={id} className="flex items-center gap-2 p-1.5 rounded bg-[hsl(var(--bg-elev))] cursor-pointer" onClick={() => handleCheck('materiel', id)}>
                <Checkbox checked={formData.materiel[id] || false} />
                <span className="text-xs text-[hsl(var(--text))]">{label}</span>
              </div>
            ))}

            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[hsl(var(--border))]">
              <div>
                <label className="text-[10px] text-[hsl(var(--text-dim))] mb-1 block">Sonde d'intubation</label>
                <Input value={formData.equipement.sondeIntubation} readOnly className="h-8 text-xs bg-[hsl(var(--bg))] font-mono text-cyan-300" />
                <p className="text-[9px] text-green-400 mt-0.5">d'après le poids</p>
              </div>
              <div>
                <label className="text-[10px] text-[hsl(var(--text-dim))] mb-1 block">Lame</label>
                <Input value={formData.equipement.lame} readOnly className="h-8 text-xs bg-[hsl(var(--bg))] font-mono text-cyan-300" />
                <p className="text-[9px] text-green-400 mt-0.5">d'après le poids</p>
              </div>
              <div>
                <label className="text-[10px] text-[hsl(var(--text-dim))] mb-1 block">VT (mL)</label>
                <Input value={formData.equipement.vt} readOnly className="h-8 text-xs bg-[hsl(var(--bg))] font-mono text-cyan-300" />
              </div>
              <div>
                <label className="text-[10px] text-[hsl(var(--text-dim))] mb-1 block">FR</label>
                <Input value={formData.equipement.fr} readOnly className="h-8 text-xs bg-[hsl(var(--bg))] font-mono text-cyan-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DROGUES */}
        <Card className="border-purple-400/30">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
              <Syringe className="w-4 h-4" />
              DROGUES
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            {/* INDUCTION */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-[hsl(var(--text))] bg-gray-500/10 p-1.5 rounded">INDUCTION</div>
              
              <div className="p-2 rounded bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">ATROPINE</span>
                  <Checkbox
                    checked={formData.medicaments.atropine.ok}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      medicaments: { ...prev.medicaments, atropine: { ...prev.medicaments.atropine, ok: checked } }
                    }))}
                  />
                </div>
                <div className="text-[10px] text-[hsl(var(--text-dim))] mb-1">systématique (enfant &lt; 1 mois)</div>
                <Badge className="bg-purple-500/20 text-purple-300 font-mono text-xs">{formData.medicaments.atropine.dose || 'Entrer poids'}</Badge>
                <div className="text-[9px] text-green-400 mt-0.5">20 µg/kg</div>
              </div>

              <div className="p-2 rounded bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">KÉTAMINE</span>
                  <Checkbox
                    checked={formData.medicaments.ketamine.ok}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      medicaments: { ...prev.medicaments, ketamine: { ...prev.medicaments.ketamine, ok: checked } }
                    }))}
                  />
                </div>
                <Badge className="bg-purple-500/20 text-purple-300 font-mono text-xs">{formData.medicaments.ketamine.dose || 'Entrer poids'}</Badge>
                <div className="text-[9px] text-green-400 mt-0.5">&lt;18 mois: 4 mg/kg | ≥18 mois: 2 mg/kg</div>
              </div>
            </div>

            {/* CURARISATION */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-[hsl(var(--text))] bg-gray-500/10 p-1.5 rounded">CURARISATION</div>
              <div className="p-2 rounded bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">CÉLOCURINE</span>
                  <Checkbox
                    checked={formData.medicaments.celocurine.ok}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      medicaments: { ...prev.medicaments, celocurine: { ...prev.medicaments.celocurine, ok: checked } }
                    }))}
                  />
                </div>
                <Badge className="bg-purple-500/20 text-purple-300 font-mono text-xs">{formData.medicaments.celocurine.dose || 'Entrer poids'}</Badge>
                <div className="text-[9px] text-green-400 mt-0.5">&lt;2 ans: 2 mg/kg | ≥2 ans: 1 mg/kg</div>
              </div>
            </div>

            {/* ENTRETIEN */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-[hsl(var(--text))] bg-gray-500/10 p-1.5 rounded">ENTRETIEN IVSE</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                  <div className="text-xs font-medium mb-1">HYPNOVEL</div>
                  <Badge className="text-green-300 font-mono text-xs">{formData.medicaments.midazolam.debit} mg/h</Badge>
                  <div className="text-[9px] text-[hsl(var(--text-dim))] mt-1">0,04-0,2 mg/kg/h</div>
                </div>
                <div className="p-2 rounded bg-[hsl(var(--bg-elev))]">
                  <div className="text-xs font-medium mb-1">SUFENTANIL</div>
                  <Badge className="text-green-300 font-mono text-xs">{formData.medicaments.sufentanil.debit} µg/h</Badge>
                  <div className="text-[9px] text-[hsl(var(--text-dim))] mt-1">0,1-0,3 µg/kg/h</div>
                </div>
              </div>
            </div>

            <Alert className="bg-red-400/10 border-red-400/30 p-2">
              <AlertDescription className="text-[10px] text-red-300">
                <strong>COMPLICATIONS :</strong><br/>
                • Laryngospasme: Succinylcholine 1-2 mg/kg ± atropine<br/>
                • Plans B et C: Bougie &gt; VL &gt; LTSD &gt; cricothyroïdotomie
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* INDUCTION-IOT */}
        <Card className="border-orange-400/30">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-orange-300 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              INDUCTION-IOT
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            <Textarea
              placeholder="Récapitulatif des posologies validées (seules les drogues précédemment cochées apparaissent)..."
              value={formData.recapPositologies}
              onChange={(e) => setFormData(prev => ({ ...prev, recapPositologies: e.target.value }))}
              className="min-h-[60px] text-xs"
            />

            <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-400/30">
              <div className="text-xs font-semibold text-blue-300 mb-1">PLANIFIER :</div>
              <div className="text-[10px] text-[hsl(var(--text))] space-y-0.5">
                <div>• Rôle-position</div>
                <div>• Communication BOUCLE FERMÉE</div>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="p-2 rounded bg-purple-500/10">
                <div className="text-xs font-semibold text-purple-300">MÉDECIN</div>
                <div className="text-[10px] text-[hsl(var(--text))]">Annonce «Prêt, dose de X mg KÉTAMINE, suivi de X mg CÉLOCURINE»</div>
              </div>
              <div className="p-2 rounded bg-green-500/10">
                <div className="text-xs font-semibold text-green-300">IDE</div>
                <div className="text-[10px] text-[hsl(var(--text))]">Répète doses, administre, annonce «KÉTA X mg, CÉLO X mg administrés»</div>
              </div>
              <div className="p-2 rounded bg-cyan-500/10">
                <div className="text-xs font-semibold text-cyan-300">CONDUCTEUR prêt pour :</div>
                <div className="text-[10px] text-[hsl(var(--text))] space-y-0.5">
                  <div>• Maintien rachis, BURP</div>
                  <div>• Passer matériel, Brancher respirateur</div>
                </div>
              </div>
            </div>

            <div className="p-2 rounded-lg bg-orange-500/10 border border-orange-400/30">
              <div className="text-xs font-semibold text-orange-300 mb-1">Attente relâchement mandibulaire (~1 min)</div>
              <div className="text-[10px] text-[hsl(var(--text))] space-y-0.5">
                <div>• Exposition, Cordes vocales visibles</div>
                <div>• Intubation jusqu'au repère noir</div>
                <div>• Gonfler ballonnet (volume adapté)</div>
                <div className="text-red-300">• 2 tentatives = stratégie alternative + renfort</div>
                <div>• Première insufflation BAVU et auscultation:</div>
                <div className="ml-3">→ épigastrique, → 2 champs pulmonaires</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* POST IOT */}
        <Card className="border-green-400/30">
          <CardHeader className="p-3 bg-[hsl(var(--bg-elev))]">
            <CardTitle className="text-sm md:text-base text-green-300 flex items-center gap-2">
              <Stethoscope className="w-4 h-4" />
              POST IOT - CONTRÔLES
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div className="space-y-1.5">
              {[
                { id: 'auscultation', label: 'Auscultation 2 champs: si sélectif → retirer 2cm', critical: true },
                { id: 'etco2', label: 'ETCO₂ courbe OK', critical: true },
                { id: 'fixation', label: 'Fixation' },
                { id: 'manometre', label: 'Manomètre (≤ 20cm H₂O)' },
                { id: 'connexion', label: 'Connexion respirateur' },
                { id: 'sondeGastrique', label: 'Sonde gastrique' },
                { id: 'entretien', label: 'Entretien : contrôler vitesse PSE' }
              ].map(({ id, label, critical }) => (
                <div
                  key={id}
                  className={`flex items-center gap-2 p-1.5 rounded cursor-pointer ${critical ? 'bg-red-500/10 border border-red-400/30' : 'bg-[hsl(var(--bg-elev))]'}`}
                  onClick={() => handleCheck('controles', id)}
                >
                  <Checkbox checked={formData.controles[id] || false} />
                  <span className={`text-xs flex-1 ${critical ? 'text-red-300 font-medium' : 'text-[hsl(var(--text))]'}`}>{label}</span>
                  {critical && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                </div>
              ))}
            </div>

            <div className="p-2 rounded-lg bg-red-500/10 border border-red-400/30">
              <div className="text-xs font-semibold text-red-300 mb-1.5">Problème adaptation (DOPE)</div>
              <div className="grid grid-cols-2 gap-1">
                {[
                  { nom: 'Débranchement', icon: AlertTriangle },
                  { nom: 'Obstruction', icon: AlertOctagon },
                  { nom: 'Pneumothorax', icon: AlertTriangle },
                  { nom: 'Sonde plicaturée', icon: AlertOctagon },
                  { nom: 'O₂ autonomie', icon: Activity },
                  { nom: 'Paramètres vent.', icon: Wind }
                ].map(({ nom, icon: Icon }) => (
                  <div key={nom} className="flex items-center gap-1 p-1 rounded bg-[hsl(var(--bg-elev))] text-[10px]">
                    <Icon className="w-3 h-3 text-red-400" />
                    <span className="text-[hsl(var(--text))]">{nom}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                { key: 'chiffreArcade', label: 'Arcade dentaire', unit: 'cm' },
                { key: 'pressionBallonnet', label: 'Pression ballonnet', unit: 'mmHg' },
                { key: 'saturation', label: 'Saturation', unit: '%' },
                { key: 'etco2', label: 'ETCO₂ initial', unit: 'mmHg' },
                { key: 'pas', label: 'PAS', unit: 'mmHg' },
                { key: 'pad', label: 'PAD', unit: 'mmHg' }
              ].map(({ key, label, unit }) => (
                <div key={key}>
                  <label className="text-[10px] text-[hsl(var(--text-dim))] mb-0.5 block truncate">{label}</label>
                  <Input
                    type="number"
                    value={formData.postiot[key]}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      postiot: { ...prev.postiot, [key]: e.target.value }
                    }))}
                    placeholder={unit}
                    className="h-7 text-xs"
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Sonde naso-gastrique</label>
              <Input
                value={formData.equipement.sondeGastrique}
                readOnly
                className="h-8 text-sm bg-[hsl(var(--bg))] font-mono text-cyan-300"
              />
              <p className="text-[9px] text-green-400 mt-0.5">d'après le poids renseigné</p>
            </div>
          </CardContent>
        </Card>

        {/* AI ANALYSIS */}
        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                ANALYSE IA - Recommandations Pédiatriques
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))]">
                  {aiAnalysis.analysis}
                </div>
              </div>
              <Alert className="mt-3 bg-amber-400/10 border-amber-400/30 p-2">
                <AlertDescription className="text-xs text-amber-300">
                  <strong>Disclaimer :</strong> Aide à la décision, validation médicale requise
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
